import React from 'react';
import { ZebraColor } from '../contexts/DisplayPreferencesContext';

interface ZebraColorPickerProps {
  label: string;
  color: ZebraColor;
  onChange: (color: ZebraColor) => void;
  isDark: boolean;
  // Lightness anchor when darkness = 0 (e.g., 94 light / 18 dark for stripes;
  // 90 / 34 for gridlines). Defaults match the stripe baselines for back-compat.
  baselineLightness?: number;
  // Saturation cap multiplier for the rendered color (stripes use 0.6 to keep
  // tints subtle on large fills; gridlines use 1.0 for thin lines).
  saturationScale?: number;
  // When true, renders a Font Lightness slider so the user can override
  // text color on rows tinted by this picker (used for stripes only).
  showFontSlider?: boolean;
  // What "Reset" restores. Defaults to the neutral zebra gray; a text-color
  // picker resets to its own default instead (neutral gray at baseline 100 is
  // white, which would reset locked text to invisible).
  resetColor?: ZebraColor;
  resetLabel?: string;
}

const DARKNESS_MIN_LIGHTNESS = 5;

function effectiveLightness(darkness: number, baseline: number): number {
  const d = Math.max(0, Math.min(100, darkness));
  return baseline - (baseline - DARKNESS_MIN_LIGHTNESS) * (d / 100);
}

export const ZebraColorPicker: React.FC<ZebraColorPickerProps> = ({
  label,
  color,
  onChange,
  isDark,
  baselineLightness,
  saturationScale = 0.6,
  showFontSlider = false,
  resetColor,
  resetLabel = 'Reset to neutral',
}) => {
  const baseline = baselineLightness ?? (isDark ? 18 : 94);
  const defaultFontLightness = isDark ? 90 : 10;
  const fontLightness = color.fontLightness ?? defaultFontLightness;

  const handleHueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ ...color, hue: parseInt(e.target.value, 10) });
  };

  const handleSaturationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ ...color, saturation: parseInt(e.target.value, 10) });
  };

  const handleDarknessChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ ...color, darkness: parseInt(e.target.value, 10) });
  };

  const handleFontLightnessChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ ...color, fontLightness: parseInt(e.target.value, 10) });
  };

  const sat = color.saturation * saturationScale;
  const lightness = effectiveLightness(color.darkness, baseline);

  const previewColor = `hsl(${color.hue}, ${sat}%, ${lightness}%)`;

  // Saturation slider gradient previews 0 -> max sat at the current lightness
  const satGradient = `linear-gradient(to right,
    hsl(${color.hue}, 0%, ${lightness}%),
    hsl(${color.hue}, ${100 * saturationScale}%, ${lightness}%)
  )`;

  // Darkness slider gradient previews baseline -> ~5% lightness at current hue/sat
  const darknessGradient = `linear-gradient(to right,
    hsl(${color.hue}, ${sat}%, ${baseline}%),
    hsl(${color.hue}, ${sat}%, ${DARKNESS_MIN_LIGHTNESS}%)
  )`;

  return (
    <div className="space-y-3">
      <label className="text-sm font-medium text-heading block">
        {label}
      </label>

      {/* Preview swatch */}
      <div className="flex items-center gap-3">
        <div
          className="w-16 h-8 rounded-md border border-gray-300 dark:border-gray-600"
          style={{ backgroundColor: previewColor }}
        />
        <span className="text-xs text-muted">
          Preview
        </span>
      </div>

      {/* Hue slider with rainbow gradient */}
      <div>
        <div className="flex justify-between mb-1">
          <span className="text-xs text-muted">Hue</span>
          <span className="text-xs text-muted">{color.hue}°</span>
        </div>
        <input
          type="range"
          min="0"
          max="360"
          value={color.hue}
          onChange={handleHueChange}
          className="w-full h-3 rounded-lg appearance-none cursor-pointer"
          style={{
            background: `linear-gradient(to right,
              hsl(0, 80%, 50%),
              hsl(60, 80%, 50%),
              hsl(120, 80%, 50%),
              hsl(180, 80%, 50%),
              hsl(240, 80%, 50%),
              hsl(300, 80%, 50%),
              hsl(360, 80%, 50%)
            )`,
          }}
        />
      </div>

      {/* Saturation slider */}
      <div>
        <div className="flex justify-between mb-1">
          <span className="text-xs text-muted">Intensity</span>
          <span className="text-xs text-muted">{color.saturation}%</span>
        </div>
        <input
          type="range"
          min="0"
          max="100"
          value={color.saturation}
          onChange={handleSaturationChange}
          className="w-full h-3 rounded-lg appearance-none cursor-pointer"
          style={{ background: satGradient }}
        />
      </div>

      {/* Darkness slider */}
      <div>
        <div className="flex justify-between mb-1">
          <span className="text-xs text-muted">Darkness</span>
          <span className="text-xs text-muted">{color.darkness}%</span>
        </div>
        <input
          type="range"
          min="0"
          max="100"
          value={color.darkness}
          onChange={handleDarknessChange}
          className="w-full h-3 rounded-lg appearance-none cursor-pointer"
          style={{ background: darknessGradient }}
        />
      </div>

      {/* Font Lightness slider — shown for stripes so users can keep text
          readable when stripe rows are dialed very dark or very light. */}
      {showFontSlider && (
        <div>
          <div className="flex justify-between mb-1">
            <span className="text-xs text-muted">Font Lightness</span>
            <span className="text-xs text-muted">{fontLightness}%</span>
          </div>
          <input
            type="range"
            min="0"
            max="100"
            value={fontLightness}
            onChange={handleFontLightnessChange}
            className="w-full h-3 rounded-lg appearance-none cursor-pointer"
            style={{
              background: `linear-gradient(to right,
                hsl(0, 0%, 0%),
                hsl(0, 0%, 100%)
              )`,
            }}
          />
          <div className="mt-1 px-2 py-1 rounded text-xs" style={{
            backgroundColor: previewColor,
            color: `hsl(0, 0%, ${fontLightness}%)`,
          }}>
            Sample text on a stripe row
          </div>
        </div>
      )}

      {/* Reset button */}
      <button
        onClick={() => onChange(resetColor ?? { hue: 220, saturation: 0, darkness: 0, fontLightness: undefined })}
        className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
      >
        {resetLabel}
      </button>
    </div>
  );
};

export default ZebraColorPicker;
